import { type CreatePasswordBody } from "@mg/schemas/src/prince/auth";
import { Button, Grid, Text, Flex, Link } from "@radix-ui/themes";
import { useMutation } from "@tanstack/react-query";
// eslint-disable-next-line import/named
import {
  useNavigate,
  useSearch,
  // eslint-disable-next-line import/named
  Link as RouterLink,
} from "@tanstack/react-router";
import { type FormEventHandler, useState, useRef, useCallback } from "react";
import zxcvbn from "zxcvbn";

import { resetPasswordRoute } from "./route";

import { LoginLayout } from "../../components/layouts/login";
import { PasswordInput } from "../../components/PasswordInput";
import { PasswordRequirements } from "../../components/PasswordRequirements";
import {
  resetWorkosPassword,
  type UpdatePasswordPayload,
} from "../../services/auth";
import { loginRoute } from "../login/route";

function useResetPasswordMutation() {
  return useMutation({
    mutationKey: ["reset-password"],
    mutationFn: resetWorkosPassword,
  });
}

export function ResetPasswordView() {
  // we won't automatically digest the token to apply the user.
  const { token } = useSearch({ from: resetPasswordRoute.to }) as {
    token: string;
  };
  const navigate = useNavigate();
  const mutation = useResetPasswordMutation();

  const formRef = useRef<HTMLFormElement>(null);
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [passwordStrength, setPasswordStrength] = useState<{
    password: string;
    score: number;
    suggestions: string[];
  }>({
    password: "",
    score: 0,
    suggestions: [],
  });
  const [passwordTouched, setPasswordTouched] = useState(false);

  const checkValidity = useCallback(() => {
    if (formRef.current == null) {
      return;
    }

    const elements = formRef.current.elements;
    // @ts-expect-error TS7015: this is totally valid
    const password = elements["password"] as HTMLInputElement;
    // @ts-expect-error TS7015: this is totally valid
    const confirmPassword = elements["confirm_password"] as HTMLInputElement;

    if (password.value !== confirmPassword.value) {
      return setPasswordsMatch(false);
    }

    return setPasswordsMatch(true);
  }, []);

  const checkPasswordLength = useCallback(() => {
    if (formRef.current == null) {
      return;
    }

    const elements = formRef.current.elements;
    // @ts-expect-error TS7015: this is totally valid
    const password = elements["password"] as HTMLInputElement;

    return password?.value.length >= 10;
  }, []);
  const handleSubmit: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();

    const form = e.target;
    const formData = new FormData(form as HTMLFormElement);
    formData.set("password", formData.get("password") as string);
    // remove the confirm_password since we've already verified they're the
    // same.
    formData.delete("confirm_password");

    // convert the FormData to a POJO
    const data = Object.fromEntries(formData.entries()) as CreatePasswordBody &
      UpdatePasswordPayload;

    setErrorMessage(""); // Clear any previous error messages

    mutation
      .mutateAsync(data)
      .then((_response) => {
        navigate({ to: loginRoute.to });
      })
      .catch((error) => {
        let errorMessage;
        if (error instanceof Error) {
          errorMessage = error.message;
        } else {
          errorMessage = `An Unknown Error ${error} occurred`;
        }
        setErrorMessage(errorMessage);
      });
  };
  return (
    <LoginLayout>
      <Text
        weight="medium"
        size="6"
        className="mb-2 block tracking-tight text-base-black"
        data-testid="reset-password-description"
      >
        Reset your password.
      </Text>

      <form
        onSubmit={handleSubmit}
        ref={formRef}
        className="grid gap-6"
        data-testid="reset-password-form"
        aria-label="Reset password"
      >
        <input
          type="hidden"
          name="token"
          value={token}
          data-testid="reset-password-token-input"
        />

        <Grid gap="2">
          <Text weight="medium">New Password</Text>
          <PasswordInput
            required
            placeholder="Enter your password"
            name="password"
            id="password"
            minLength={10}
            disabled={mutation.isPending}
            autoComplete="new-password"
            data-testid="password-field"
            onChange={(e) => {
              if (e.target.value.length === 0) {
                setPasswordTouched(false);
              } else if (!passwordTouched) {
                setPasswordTouched(true);
              }

              try {
                const evaluation = zxcvbn(e.target.value);
                setPasswordStrength({
                  suggestions: evaluation.feedback.suggestions,
                  score: evaluation.score,
                  password: e.target.value,
                });
              } catch (error) {
                console.error("Password strength evaluation failed:", error);
                // Fallback to basic validation
                setPasswordStrength({
                  suggestions: [],
                  score: e.target.value.length >= 10 ? 2 : 0,
                  password: e.target.value,
                });
              }
            }}
            error={
              passwordTouched &&
              (mutation.isError ||
                !checkPasswordLength() ||
                passwordStrength.score < 3)
            }
          />
        </Grid>

        <PasswordRequirements
          strength={passwordStrength}
          showInitialRequirements={!passwordTouched}
        />

        {passwordTouched && !checkPasswordLength() && (
          <Text size="2" className="text-puntt-red-10">
            Password must be at least 10 characters long
          </Text>
        )}

        <Grid gap="2">
          <Text weight="medium">Verify New Password</Text>
          <PasswordInput
            required
            placeholder="Enter password again"
            variant="classic"
            onInput={checkValidity}
            name="confirm_password"
            id="confirm_password"
            minLength={10}
            disabled={mutation.isPending}
            autoComplete="new-password"
            error={!passwordsMatch}
            data-testid="confirm-password-field"
          />

          {!passwordsMatch && (
            <Flex align="center" gap="2">
              <Text size="2" className="text-puntt-red-10">
                Passwords do not match
              </Text>
            </Flex>
          )}
        </Grid>

        {mutation.isError && (
          <Text
            size="2"
            className="text-puntt-red-dark-5"
            data-testid="password-error-message"
          >
            {mutation.error?.message ?? errorMessage}
          </Text>
        )}

        <Button
          type="submit"
          disabled={
            !passwordsMatch ||
            mutation.isPending ||
            passwordStrength?.score < 3 ||
            !checkPasswordLength()
          }
          loading={mutation.isPending}
          data-testid="form-submit-cta"
        >
          Reset Password
        </Button>

        <RouterLink to={loginRoute.to} data-testid="login-existing-link">
          <Link size="2" className="puntt-link underline-offset-4">
            <span className="underline decoration-puntt-accent-5">Log In</span>{" "}
            to another account
          </Link>
        </RouterLink>
      </form>
    </LoginLayout>
  );
}
